import React from 'react'
import {
  getProviderCodeFromName,
  getRoundedNightlyDisplayTotalPrice
} from 'widgets/helpers/offer'

import {Divider} from '@daedalus/atlas/Divider'
import {Grid} from '@daedalus/atlas/helpers/Grid'
import {Icon} from '@daedalus/atlas/Icon'
import {PriceComposition} from '@daedalus/atlas/PriceComposition'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'
import {ProviderLogo} from '@findhotel/shared/src/search/ProviderLogo'
import {ProviderName} from '@findhotel/shared/src/search/ProviderName'

import {Offer} from '../../../../types/partnersApi/types'
import {getOfferDDActionName} from '../../../../utils/getOfferDDActionName'
import {OfferInteraction} from '../OfferInteraction'
import {RoomLabels} from '../Primary/RoomLabels'
import {Skeleton} from '.'
import {
  IconWrapper,
  OfferContainer,
  OfferListWrapper,
  OfferWrapper,
  PriceWrapper,
  ProviderWrapper
} from './styles'

// This should be moved to the new partners package env vars
const baseUrl = 'https://i.fih.io/provider'

interface SecondaryOfferProps {
  offer: Offer
  price: number
  providerCode: string
  currency: string
  hasFreeCancellation?: boolean
  anchorPrice?: number
  isLastOffer?: boolean
  position: number
  onClick?: () => void
}

export const SecondaryOffer = ({
  offer,
  price,
  currency,
  providerCode,
  anchorPrice = 0,
  isLastOffer = true,
  position,
  onClick
}: SecondaryOfferProps) => {
  if (!offer) return <Skeleton />

  return (
    <OfferInteraction offer={offer} onClick={onClick}>
      <OfferContainer data-dd-action-name={getOfferDDActionName(position)}>
        <OfferWrapper
          container
          wrap="nowrap"
          alignItems="center"
          justify="space-between"
          spacing="s000"
        >
          <Grid container direction="column" spacing="s000">
            <ProviderWrapper
              container
              direction="row"
              gap="s200"
              wrap="nowrap"
              spacing="s000"
            >
              <ProviderLogo
                providerCode={providerCode}
                urlPrefix={baseUrl}
                variant="icon"
              />
              <ProviderName providerCode={providerCode} size="md" />
            </ProviderWrapper>
            <RoomLabels isAlternativeColor offer={offer} />
          </Grid>
          <PriceWrapper
            container
            alignItems="center"
            justify="flex-end"
            spacing="s000"
          >
            <div>
              <PriceComposition
                anchorPrice={anchorPrice}
                currencyCode={currency}
                price={price}
                size="sm"
                showAnchorPrice={false}
              />
            </div>
            <IconWrapper>
              <Icon name="ChevronRight" size="sm" />
            </IconWrapper>
          </PriceWrapper>
        </OfferWrapper>
        {!isLastOffer && <Divider sideMargin="s400" />}
      </OfferContainer>
    </OfferInteraction>
  )
}

interface SecondaryOfferListProps {
  offers: Offer[]
  numberOfRooms: number
  numberOfNights: number
  taxDisplayLogic: TaxDisplayLogic
  anchorPrice?: number
  onOfferClick: () => void
}
export const SecondaryOfferList = ({
  offers,
  numberOfRooms,
  numberOfNights,
  taxDisplayLogic,
  anchorPrice = 0,
  onOfferClick
}: SecondaryOfferListProps) => {
  if (!offers)
    return (
      <>
        <Skeleton />
        <Divider sideMargin="s200" />
        <Skeleton />
      </>
    )
  return (
    <OfferListWrapper
      container
      spacing="s000"
      direction="column"
      justify="flex-start"
    >
      {offers.map((offer, index) => {
        const {currency, rate, provider} = offer

        const price = getRoundedNightlyDisplayTotalPrice({
          rate: rate,
          numberOfNights,
          numberOfRooms,
          taxDisplayLogic
        })

        const providerCode = getProviderCodeFromName(provider.name)
        return (
          <SecondaryOffer
            key={index}
            currency={currency}
            price={price}
            providerCode={providerCode}
            anchorPrice={anchorPrice}
            offer={offer}
            isLastOffer={index === offers.length - 1}
            position={index + 2}
            onClick={onOfferClick}
          />
        )
      })}
    </OfferListWrapper>
  )
}
